import { useEffect, useState } from 'react';
import { Button, Label } from '@lognai/synapse-design-system-v2';
import { hasPermission } from '@lognai/plugin-sdk';
import { useAuth } from '../hooks/useAuth';
import { useUpdateCompanyBrandColor } from '../hooks/useCompanyBrandColor';
import { normalizeHex, setCompanyPrimaryColor } from '../utils/company-primary-color';

// Matches `colors.primary[500]` in the design system — the brand indigo.
const DEFAULT_COLOR = '#4865da';
const PRESETS = ['#4865da', '#8450c9', '#c03a63', '#d22e2c', '#c2571e', '#1c8f5f', '#0083a8', '#292d39'];

/**
 * The company's theme colour, in Settings → Appearance. It applies to everyone in
 * the company, so a company admin previews a pick and saves it explicitly; everyone
 * else sees it read-only. A platform admin with no company selected has nothing to set.
 */
export function CompanyColorPicker() {
  const { company } = useAuth();
  const canEdit = hasPermission('admin:company:update');
  const saved = company?.branding?.primaryColor ?? null;
  const savedDark = company?.branding?.primaryColorDark ?? null;
  const [draft, setDraft] = useState(saved ?? DEFAULT_COLOR);
  const update = useUpdateCompanyBrandColor();

  useEffect(() => setDraft(saved ?? DEFAULT_COLOR), [saved]);

  // The preview paints the page directly; leaving Settings unsaved puts the company colour back.
  useEffect(
    () => () => setCompanyPrimaryColor(saved ? { light: saved, dark: savedDark } : null),
    [saved, savedDark]
  );

  if (!company) return null;

  const preview = (next: string) => {
    setDraft(next);
    const hex = normalizeHex(next);
    if (hex) setCompanyPrimaryColor({ light: hex, dark: savedDark });
  };
  const cancel = () => {
    setDraft(saved ?? DEFAULT_COLOR);
    setCompanyPrimaryColor(saved ? { light: saved, dark: savedDark } : null);
  };
  const save = (brandColor: string | null) => {
    update.reset();
    update.mutate({
      companyId: company.id,
      colors: brandColor ? { brandColor } : { brandColor: null, brandColorDark: null },
    });
  };

  const dirty = normalizeHex(draft) !== (saved ?? DEFAULT_COLOR);

  if (!canEdit) {
    return (
      <div>
        <Label>Primary color</Label>
        <div className="mt-2 flex items-center gap-3">
          <span
            className="h-7 w-7 rounded-full border border-border"
            style={{ backgroundColor: saved ?? DEFAULT_COLOR }}
          />
          <span className="text-sm text-muted-foreground">
            Set for everyone at {company.name} by your company administrator.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="primary-color-picker">Primary color</Label>
        <p className="text-xs text-muted-foreground">Applies to everyone at {company.name}.</p>
        <input
          id="primary-color-picker"
          type="color"
          value={normalizeHex(draft) ?? DEFAULT_COLOR}
          onChange={(e) => preview(e.target.value)}
          className="mt-2 h-10 w-14 cursor-pointer rounded-md border border-border bg-surface p-1"
        />
      </div>

      <div>
        <Label>Presets</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {PRESETS.map((hex) => (
            <button
              key={hex}
              type="button"
              aria-label={hex}
              onClick={() => preview(hex)}
              className="h-7 w-7 rounded-full border border-border ring-offset-2 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ backgroundColor: hex }}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => save(normalizeHex(draft))} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save for company'}
        </Button>
        {dirty && (
          <Button variant="outline" size="sm" onClick={cancel} disabled={update.isPending}>
            Cancel
          </Button>
        )}
        {saved && !dirty && (
          <Button variant="outline" size="sm" onClick={() => save(null)} disabled={update.isPending}>
            Reset to default
          </Button>
        )}
        {update.isSuccess && !dirty && <span className="text-sm text-muted-foreground">Saved for {company.name}.</span>}
        {update.isError && (
          <span className="text-sm text-destructive">
            {update.error instanceof Error ? update.error.message : 'Save failed.'}
          </span>
        )}
      </div>
    </div>
  );
}
